import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';

const ThreeDSModal = ({ isOpen, redirectUrl, onClose, onSuccess, onFailure }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeSrc, setIframeSrc] = useState(null);
  const blobUrlRef = useRef(null);

  // Build iframe src: HTML content → blob URL, plain URL → use directly
  useEffect(() => {
    if (!isOpen || !redirectUrl) {
      setIsLoading(true);
      setIframeSrc(null);
      return;
    }

    // NicePay 3DS returns an HTML form string, not a URL
    if (redirectUrl.trim().startsWith('<')) {
      const blob = new Blob([redirectUrl], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setIframeSrc(url);
    } else {
      setIframeSrc(redirectUrl);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, redirectUrl]);

  // Listen for postMessage from iframe (fallback completion signal)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === '3ds_complete') {
        if (event.data.success) {
          onSuccess();
        } else {
          onFailure(event.data.resultMessage || 'Payment verification failed');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess, onFailure]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold" style={{ color: "#3B3B3B" }}>Secure Payment Verification</h2>
            <p className="text-xs sm:text-sm mt-1" style={{ color: "#515164" }}>Please complete the verification process</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" style={{ color: "#515164" }} />
          </button>
        </div>

        {/* Iframe Container */}
        <div className="flex-1 relative overflow-hidden" style={{ minHeight: '300px' }}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: "#F9FAFC" }}>
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: "#01793B", borderRightColor: "transparent" }}></div>
                <p className="text-xs sm:text-sm font-medium" style={{ color: "#515164" }}>Loading secure verification...</p>
              </div>
            </div>
          )}

          {iframeSrc && (
            <iframe
              src={iframeSrc}
              className="w-full h-full border-0"
              style={{ minHeight: '300px' }}
              title="3DS Authentication"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-center" style={{ backgroundColor: "#F9FAFC" }}>
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            Secured by your card provider. Please do not close this window.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThreeDSModal;
